import { useEffect, useRef, useState } from "react";
import { fetchMediaBlob } from "../api";
import type { Frame } from "../lib/frames";

type Props = {
  src: string;
  frame: Frame;
  onCancel: () => void;
  onDone: (blob: Blob) => void;
};

export default function MediaCropper({ src, frame, onCancel, onDone }: Props) {
  const view = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [show, setShow] = useState("");
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [ox, setOx] = useState(0);
  const [oy, setOy] = useState(0);
  const [ready, setReady] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    let dead = false;
    let blob = "";
    setReady(false);
    setShow("");
    void (async () => {
      const url = /^(blob:|data:)/i.test(src) ? src : await fetchMediaBlob(src);
      if (dead) {
        if (url.startsWith("blob:") && url !== src) URL.revokeObjectURL(url);
        return;
      }
      if (url.startsWith("blob:") && url !== src) blob = url;
      const img = new Image();
      img.onload = () => {
        if (dead) return;
        imgRef.current = img;
        setShow(url);
        setReady(true);
      };
      img.onerror = () => {
        if (!dead) setReady(false);
      };
      img.src = url;
    })().catch(() => {
      if (!dead) setReady(false);
    });
    return () => {
      dead = true;
      if (blob) URL.revokeObjectURL(blob);
    };
  }, [src]);

  const ratio = frame.w / frame.h;
  const viewW = 320;
  const viewH = Math.round(320 / ratio);

  function cover(): number {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return 1;
    return Math.max(viewW / img.naturalWidth, viewH / img.naturalHeight);
  }

  function onPointerDown(e: React.PointerEvent) {
    drag.current = { x: e.clientX, y: e.clientY, ox, oy };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    setOx(drag.current.ox + (e.clientX - drag.current.x));
    setOy(drag.current.oy + (e.clientY - drag.current.y));
  }
  function onPointerUp() {
    drag.current = null;
  }

  function exportCrop() {
    const img = imgRef.current;
    if (!img) return;
    const canvas = document.createElement("canvas");
    canvas.width = frame.w;
    canvas.height = frame.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rx = frame.w / viewW;
    const scale = cover() * zoom;
    ctx.fillStyle = "#111";
    ctx.fillRect(0, 0, frame.w, frame.h);
    ctx.translate(frame.w / 2 + ox * rx, frame.h / 2 + oy * rx);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(scale * rx, scale * rx);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    canvas.toBlob(
      (b) => {
        if (b) onDone(b);
      },
      "image/jpeg",
      0.92,
    );
  }

  return (
    <div className="pr-card space-y-4 p-5">
      <div>
        <h3 className="text-lg font-semibold">Recadrer</h3>
        <p className="mt-1 text-sm text-slate-500">
          Cadre {frame.label} · {frame.w}×{frame.h} · {frame.hint}
        </p>
      </div>
      <div
        ref={view}
        className="relative mx-auto overflow-hidden rounded-2xl bg-mist"
        style={{ width: viewW, height: viewH, touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {ready && show ? (
          <img
            src={show}
            alt=""
            draggable={false}
            className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
            style={{
              width: imgRef.current ? imgRef.current.naturalWidth * cover() * zoom : viewW,
              height: imgRef.current ? imgRef.current.naturalHeight * cover() * zoom : viewH,
              transform: `translate(calc(-50% + ${ox}px), calc(-50% + ${oy}px)) rotate(${rot}deg)`,
            }}
          />
        ) : (
          <p className="flex h-full items-center justify-center text-xs text-slate-500">Chargement…</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex-1 text-xs text-slate-500">
          Zoom
          <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="mt-1 w-full" />
        </label>
        <button type="button" className="pr-btn-ghost" onClick={() => setRot((r) => r + 90)}>
          Pivoter
        </button>
      </div>
      <p className="text-xs text-slate-500">Glissez le visuel dans le cadre. Le cadre respecte le réseau.</p>
      <div className="flex gap-2">
        <button type="button" className="pr-btn" onClick={exportCrop} disabled={!ready}>
          Valider le cadre
        </button>
        <button type="button" className="pr-btn-ghost" onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}
